import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ArrowRight, BookOpen, Scale, Home, X, ChevronDown, ExternalLink, Hash, FileText, Tag, Check, Loader2 } from 'lucide-react';
import { Button } from "../components/ui/button";
import ThemeToggle from "../components/ThemeToggle";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Import comprehensive section mappings from JSON
import sectionMappingsData from '../data/sectionMappings.json';

// ═══════════════════════════════════════════════════════════════════════════════
// MARKDOWN RENDERER WITH STYLED TABLES
// ═══════════════════════════════════════════════════════════════════════════════
const MarkdownContent = ({ content }) => {
  return (
    <div className="markdown-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          table: ({ children }) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full border-collapse border border-slate-600 text-sm">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-slate-700/50">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="divide-y divide-slate-700">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-slate-700/30 transition-colors">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-3 py-2 text-left text-xs font-semibold text-amber-400 border border-slate-600 whitespace-nowrap">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-3 py-2 text-slate-300 border border-slate-600 text-xs">
              {children}
            </td>
          ),
          p: ({ children }) => (
            <p className="mb-3 leading-relaxed">{children}</p>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-white">{children}</strong>
          ),
          h1: ({ children }) => (
            <h1 className="text-xl font-bold text-white mb-3 mt-4">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-lg font-bold text-white mb-2 mt-3">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-base font-semibold text-white mb-2 mt-2">{children}</h3>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="text-slate-300">{children}</li>
          ),
          a: ({ href, children }) => (
            <a href={href} className="text-cyan-400 hover:text-cyan-300 underline" target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-amber-500 pl-4 my-3 italic text-slate-400">
              {children}
            </blockquote>
          ),
          code: ({ children }) => (
            <code className="bg-slate-700 px-1 py-0.5 rounded text-amber-300 text-xs">
              {children}
            </code>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

import { API_BASE_URL } from "../utils/api";
const API_URL = API_BASE_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// SEARCHABLE DROPDOWN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder, 
  label,
  colorScheme = 'cyan',
  renderOption,
  searchField = 'value',
  displayField = 'label',
  testId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options.slice(0, 100); // Limit initial display
    const term = searchTerm.toLowerCase();
    return options.filter(opt => {
      const searchValue = typeof opt === 'string' ? opt : opt[searchField];
      const displayValue = typeof opt === 'string' ? opt : opt[displayField];
      return searchValue?.toLowerCase().includes(term) || displayValue?.toLowerCase().includes(term);
    }).slice(0, 100);
  }, [options, searchTerm, searchField, displayField]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const colorClasses = {
    cyan: { border: 'border-cyan-500', ring: 'ring-cyan-500', hover: 'hover:bg-cyan-500/20', selected: 'bg-cyan-500/30' },
    amber: { border: 'border-amber-500', ring: 'ring-amber-500', hover: 'hover:bg-amber-500/20', selected: 'bg-amber-500/30' },
    purple: { border: 'border-purple-500', ring: 'ring-purple-500', hover: 'hover:bg-purple-500/20', selected: 'bg-purple-500/30' }
  };

  const colors = colorClasses[colorScheme];
  const selectedOption = options.find(opt => (typeof opt === 'string' ? opt : opt.value) === value);
  const displayValue = selectedOption ? (typeof selectedOption === 'string' ? selectedOption : selectedOption[displayField]) : '';

  return (
    <div className="relative" ref={dropdownRef}>
      {label && <label className="block text-sm font-medium text-slate-400 mb-2">{label}</label>}
      <div
        className={`w-full bg-slate-800 border ${isOpen ? colors.border : 'border-slate-600'} rounded-xl px-4 py-3 cursor-pointer transition-all duration-200 ${isOpen ? `ring-2 ${colors.ring}` : ''}`}
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) setTimeout(() => inputRef.current?.focus(), 100); }}
        data-testid={testId}
      >
        <div className="flex items-center justify-between">
          <span className={`${value ? 'text-white' : 'text-slate-500'} truncate`}>
            {displayValue || placeholder}
          </span>
          <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-2 bg-slate-800 border border-slate-600 rounded-xl shadow-2xl overflow-hidden"
          >
            <div className="p-3 border-b border-slate-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Type to search..."
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-10 pr-4 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const optValue = typeof option === 'string' ? option : option.value;
                  const isSelected = optValue === value;
                  
                  return (
                    <div
                      key={`${optValue}-${index}`}
                      className={`px-4 py-3 cursor-pointer transition-colors ${isSelected ? colors.selected : colors.hover} ${isSelected ? 'text-white' : 'text-slate-300'}`}
                      onClick={(e) => { e.stopPropagation(); onChange(optValue); setIsOpen(false); setSearchTerm(''); }}
                    >
                      {renderOption ? renderOption(option, isSelected) : (
                        <div className="flex items-center justify-between">
                          <span className="text-sm">{typeof option === 'string' ? option : option[displayField]}</span>
                          {isSelected && <Check className="w-4 h-4 text-green-400" />}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-6 text-center text-slate-500 text-sm">No sections found</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION MAPPER PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const SectionMapperPage = () => {
  const [selected1961, setSelected1961] = useState('');
  const [selected2025, setSelected2025] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [keywordResults, setKeywordResults] = useState([]);
  const [activeSearch, setActiveSearch] = useState(null);
  
  // Content loading states
  const [summaryContent, setSummaryContent] = useState('');
  const [bareActContent, setBareActContent] = useState('');
  const [bareAct1961Content, setBareAct1961Content] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingBareAct, setLoadingBareAct] = useState(false);
  const [loadingBareAct1961, setLoadingBareAct1961] = useState(false);

  const allSections = useMemo(() => sectionMappingsData.sections || [], []);

  // Build dropdown options for 1961 Act
  const options1961 = useMemo(() => {
    return allSections.map(section => ({
      value: section.section1961,
      label: `Section ${section.section1961} - ${section.title1961.substring(0, 50)}${section.title1961.length > 50 ? '...' : ''}`,
      section: section
    }));
  }, [allSections]);

  // Build dropdown options for 2025 Act with NEW badge logic
  const options2025 = useMemo(() => {
    const uniqueSections = new Map();
    allSections.forEach(section => {
      if (section.section2025 && !['N/A', 'Omitted', 'Redundant', '-'].includes(section.section2025)) {
        if (!uniqueSections.has(section.section2025)) {
          uniqueSections.set(section.section2025, {
            value: section.section2025,
            label: `Section ${section.section2025}`,
            title: section.title1961,
            isNew: section.isNew || ['NEW', 'N/A', '-'].includes(section.section1961.toUpperCase()),
            section: section
          });
        }
      }
    });
    return Array.from(uniqueSections.values()).sort((a, b) => {
      const aNum = parseInt(a.value);
      const bNum = parseInt(b.value);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      return a.value.localeCompare(b.value);
    });
  }, [allSections]);

  // Fetch section content from API (ITA 2025)
  const fetchSectionContent = useCallback(async (section2025, contentType) => {
    try {
      const response = await fetch(`${API_URL}/api/section-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section_2025: section2025, content_type: contentType })
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching section content:', error);
      return { found: false, content: 'Error loading content.' };
    }
  }, []);

  // Fetch section content from API (ITA 1961 - VERBATIM)
  const fetchSection1961Content = useCallback(async (section1961) => {
    try {
      const response = await fetch(`${API_URL}/api/section-content-1961`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section_1961: section1961 })
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching ITA 1961 section content:', error);
      return { found: false, content: 'Error loading ITA 1961 content.' };
    }
  }, []);

  // Load summary when mapping is selected
  useEffect(() => {
    if (selectedMapping && selectedMapping.section2025) {
      setLoadingSummary(true);
      setSummaryContent('');
      fetchSectionContent(selectedMapping.section2025, 'summary')
        .then(data => {
          setSummaryContent(data.content || 'Summary not available.');
          setLoadingSummary(false);
        });
    }
  }, [selectedMapping, fetchSectionContent]);

  // Load bare act when comparison is shown (both ITA 2025 and ITA 1961)
  useEffect(() => {
    if (showComparison && selectedMapping && selectedMapping.section2025) {
      // Load ITA 2025 content
      setLoadingBareAct(true);
      setBareActContent('');
      fetchSectionContent(selectedMapping.section2025, 'bare_act')
        .then(data => {
          setBareActContent(data.content || 'Bare act text not available.');
          setLoadingBareAct(false);
        });
      
      // Load ITA 1961 content (VERBATIM)
      setLoadingBareAct1961(true);
      setBareAct1961Content('');
      fetchSection1961Content(selectedMapping.section1961)
        .then(data => {
          setBareAct1961Content(data.content || 'ITA 1961 text not available.');
          setLoadingBareAct1961(false);
        });
    }
  }, [showComparison, selectedMapping, fetchSectionContent, fetchSection1961Content]);

  const handleSelect1961 = useCallback((value) => {
    setSelected1961(value);
    setSelected2025('');
    setKeywordResults([]);
    setShowComparison(false);
    setActiveSearch('act1961');
    setSummaryContent('');
    setBareActContent('');
    setBareAct1961Content('');
    
    const mapping = allSections.find(s => s.section1961 === value);
    if (mapping) setSelectedMapping(mapping);
  }, [allSections]);

  const handleSelect2025 = useCallback((value) => {
    setSelected2025(value);
    setSelected1961('');
    setKeywordResults([]);
    setShowComparison(false);
    setActiveSearch('act2025');
    setSummaryContent('');
    setBareActContent('');
    setBareAct1961Content('');
    
    const mapping = allSections.find(s => s.section2025 === value);
    if (mapping) setSelectedMapping(mapping);
  }, [allSections]);

  const handleKeywordSearch = useCallback(() => {
    if (!searchKeyword.trim()) {
      setKeywordResults([]);
      setSelectedMapping(null);
      setActiveSearch(null);
      return;
    }
    
    const keywords = searchKeyword.trim().toLowerCase().split(/\s+/);
    const results = allSections.filter(section => {
      const title = section.title1961.toLowerCase();
      const sec1961 = section.section1961.toLowerCase();
      const sec2025 = section.section2025.toLowerCase();
      return keywords.some(kw => title.includes(kw) || sec1961.includes(kw) || sec2025.includes(kw));
    });
    
    results.sort((a, b) => {
      const aMatches = keywords.filter(kw => a.title1961.toLowerCase().includes(kw)).length;
      const bMatches = keywords.filter(kw => b.title1961.toLowerCase().includes(kw)).length;
      return bMatches - aMatches;
    });
    
    setKeywordResults(results);
    setSelectedMapping(null);
    setSelected1961('');
    setSelected2025('');
    setActiveSearch('keyword');
    setShowComparison(false);
  }, [searchKeyword, allSections]);

  const handleSelectFromKeywordResults = (mapping) => {
    setSelectedMapping(mapping);
    setShowComparison(false);
    setSummaryContent('');
    setBareActContent('');
  };

  const handleClearAll = () => {
    setSelected1961('');
    setSelected2025('');
    setSearchKeyword('');
    setSelectedMapping(null);
    setKeywordResults([]);
    setShowComparison(false);
    setActiveSearch(null);
    setSummaryContent('');
    setBareActContent('');
  };

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
        <div className="w-[95%] max-w-none mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 text-white hover:text-cyan-400 transition-colors">
              <Scale className="w-8 h-8 text-cyan-400" />
              <span className="text-xl font-bold">TaxSetu AI</span>
            </Link>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <Home className="w-5 h-5" />
                <span className="hidden sm:inline">Home</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-8 px-4">
        <div className="w-[95%] max-w-none mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 rounded-full text-cyan-400 text-sm mb-6">
              <BookOpen className="w-4 h-4" />
              <span>Official Section Mapping Utility</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">IT Section Mapper</h1>
            <p className="text-base md:text-lg text-slate-400 max-w-4xl mx-auto mb-2">
              Utility to check Section of <span className="text-cyan-400 font-medium">Income-tax Act, 1961</span> vis-a-vis 
              corresponding Section of <span className="text-amber-400 font-medium">Income-tax Act, 2025</span>
            </p>
            <p className="text-sm text-slate-500">Database: {sectionMappingsData.metadata?.totalSections || allSections.length} sections mapped</p>
          </motion.div>
        </div>
      </section>

      {/* Three Rectangle Search UI */}
      <section className="py-6 px-4">
        <div className="w-[95%] max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Box 1: ITA 1961 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className={`bg-slate-800/60 rounded-2xl border-2 p-5 transition-all duration-300 ${activeSearch === 'act1961' ? 'border-cyan-500 shadow-lg shadow-cyan-500/20' : 'border-slate-700 hover:border-slate-600'}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <Hash className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">ITA 1961</h3>
                  <p className="text-xs text-slate-400">Select section number</p>
                </div>
              </div>
              
              <SearchableDropdown
                options={options1961}
                value={selected1961}
                onChange={handleSelect1961}
                placeholder="Select Section (ITA 1961)"
                colorScheme="cyan"
                searchField="value"
                displayField="label"
                testId="dropdown-1961"
                renderOption={(option, isSelected) => (
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <span className="text-cyan-400 font-medium">Section {option.value}</span>
                      <span className="text-slate-400 text-xs ml-2 block truncate">{option.section.title1961}</span>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-green-400 flex-shrink-0 ml-2" />}
                  </div>
                )}
              />
            </motion.div>

            {/* Box 2: ITA 2025 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className={`bg-slate-800/60 rounded-2xl border-2 p-5 transition-all duration-300 ${activeSearch === 'act2025' ? 'border-amber-500 shadow-lg shadow-amber-500/20' : 'border-slate-700 hover:border-slate-600'}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">ITA 2025</h3>
                  <p className="text-xs text-slate-400">Select new section number</p>
                </div>
              </div>
              
              <SearchableDropdown
                options={options2025}
                value={selected2025}
                onChange={handleSelect2025}
                placeholder="Select Section (ITA 2025)"
                colorScheme="amber"
                searchField="value"
                displayField="label"
                testId="dropdown-2025"
                renderOption={(option, isSelected) => (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-amber-400 font-medium">Section {option.value}</span>
                      {option.isNew && (
                        <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs font-semibold rounded border border-green-500/30">NEW</span>
                      )}
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-green-400 flex-shrink-0" />}
                  </div>
                )}
              />
            </motion.div>

            {/* Box 3: Keyword Search */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className={`bg-slate-800/60 rounded-2xl border-2 p-5 transition-all duration-300 ${activeSearch === 'keyword' ? 'border-purple-500 shadow-lg shadow-purple-500/20' : 'border-slate-700 hover:border-slate-600'}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Tag className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Search by Keywords</h3>
                  <p className="text-xs text-slate-400">Don't remember the section?</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleKeywordSearch()}
                  placeholder="e.g., HRA, Rent, TDS, Salary..."
                  className="flex-1 bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  data-testid="search-keyword-input"
                />
                <Button onClick={handleKeywordSearch} className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-3 rounded-xl" data-testid="search-keyword-btn">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          </div>

          {(selected1961 || selected2025 || searchKeyword || selectedMapping) && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mt-4">
              <button onClick={handleClearAll} className="text-slate-400 hover:text-white text-sm flex items-center gap-2 mx-auto transition-colors" data-testid="clear-all-btn">
                <X className="w-4 h-4" /> Clear all selections
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Results Section */}
      <section className="py-6 px-4">
        <div className="w-[95%] max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            
            {/* Summary Card with Quick View */}
            {selectedMapping && !showComparison && (
              <motion.div key="summary-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
                <div className="bg-gradient-to-r from-slate-800/80 to-slate-800/40 rounded-2xl border border-slate-700 p-6 mb-6">
                  <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 w-full lg:w-auto">
                      <div className="text-center">
                        <span className="text-xs text-cyan-400 uppercase tracking-wider block mb-1">ITA 1961</span>
                        <span className="text-xl md:text-2xl font-bold text-white bg-cyan-500/20 px-4 py-2 rounded-xl border border-cyan-500/30 inline-block">
                          Section {selectedMapping.section1961}
                        </span>
                      </div>
                      <ArrowRight className="w-6 h-6 text-slate-500 hidden sm:block flex-shrink-0" />
                      <div className="text-center">
                        <span className="text-xs text-amber-400 uppercase tracking-wider block mb-1">ITA 2025</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xl md:text-2xl font-bold text-white bg-amber-500/20 px-4 py-2 rounded-xl border border-amber-500/30 inline-block">
                            Section {selectedMapping.section2025}
                          </span>
                          {(selectedMapping.isNew || selectedMapping.section1961.toUpperCase() === 'NEW') && (
                            <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded-full border border-green-500/30">NEW</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="w-full lg:w-auto lg:text-right">
                      <p className="text-slate-300 text-sm mb-4 max-w-md">{selectedMapping.title1961}</p>
                      <Button onClick={() => setShowComparison(true)} className="btn-primary px-6 py-3 text-white font-semibold rounded-xl w-full sm:w-auto" data-testid="compare-btn">
                        <BookOpen className="w-5 h-5 mr-2" />
                        Compare Bare Act Language
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Quick View Summary */}
                <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-400" />
                    Quick Summary (ITA 2025 - Section {selectedMapping.section2025})
                  </h3>
                  {loadingSummary ? (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Loading summary...
                    </div>
                  ) : (
                    <div className="text-slate-300 text-sm max-h-64 overflow-y-auto">
                      <MarkdownContent content={summaryContent || 'Summary loading...'} />
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Split-Screen Comparison View */}
            {showComparison && selectedMapping && (
              <motion.div key="comparison-view" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.4 }}>
                <div className="bg-gradient-to-r from-slate-800/80 to-slate-800/40 rounded-2xl border border-slate-700 p-4 mb-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <span className="text-cyan-400 font-semibold">Section {selectedMapping.section1961}</span>
                      <ArrowRight className="w-5 h-5 text-slate-500" />
                      <span className="text-amber-400 font-semibold">Section {selectedMapping.section2025}</span>
                    </div>
                    <button onClick={() => setShowComparison(false)} className="text-slate-400 hover:text-white text-sm transition-colors">
                      ← Back to Summary
                    </button>
                  </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-6 mb-6">
                  {/* Pane A - ITA 1961 (VERBATIM LEGAL TEXT) */}
                  <div className="bg-slate-800/50 rounded-2xl border border-cyan-500/30 overflow-hidden">
                    <div className="bg-cyan-600/20 px-6 py-4 border-b border-cyan-500/30">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xs text-cyan-300 uppercase tracking-wider">Income Tax Act, 1961</span>
                          <h2 className="text-xl font-bold text-white mt-1">Section {selectedMapping.section1961}</h2>
                        </div>
                        <a href="https://incometaxindia.gov.in/Pages/acts/income-tax-act.aspx" target="_blank" rel="noopener noreferrer" className="p-2 text-cyan-400 hover:text-cyan-300 transition-colors">
                          <ExternalLink className="w-5 h-5" />
                        </a>
                      </div>
                      <p className="text-cyan-200 text-sm mt-2">{selectedMapping.title1961}</p>
                    </div>
                    <div className="p-6 min-h-[300px] max-h-[500px] overflow-y-auto">
                      {loadingBareAct1961 ? (
                        <div className="flex items-center justify-center gap-2 text-slate-400 py-8">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Loading ITA 1961 bare act text...
                        </div>
                      ) : selectedMapping.section1961.toUpperCase() === 'NEW' || selectedMapping.section1961 === '-' ? (
                        <div className="text-slate-400 text-sm text-center py-8">
                          <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                          <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm font-semibold rounded-full border border-green-500/30">NEW PROVISION</span>
                          <p className="mt-4">This section is a new provision in ITA 2025.</p>
                          <p className="text-xs mt-2">No corresponding section exists in ITA 1961.</p>
                        </div>
                      ) : (
                        <div className="text-slate-300 text-sm">
                          <MarkdownContent content={bareAct1961Content || 'Loading...'} />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pane B - ITA 2025 */}
                  <div className="bg-slate-800/50 rounded-2xl border border-amber-500/30 overflow-hidden">
                    <div className="bg-amber-600/20 px-6 py-4 border-b border-amber-500/30">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xs text-amber-300 uppercase tracking-wider">Income Tax Act, 2025</span>
                          <h2 className="text-xl font-bold text-white mt-1">Section {selectedMapping.section2025}</h2>
                        </div>
                        <a href="https://incometaxindia.gov.in/Pages/budget-and-bills/finance-bill.aspx" target="_blank" rel="noopener noreferrer" className="p-2 text-amber-400 hover:text-amber-300 transition-colors">
                          <ExternalLink className="w-5 h-5" />
                        </a>
                      </div>
                      <p className="text-amber-200 text-sm mt-2">{selectedMapping.title1961}</p>
                    </div>
                    <div className="p-6 min-h-[300px] max-h-[500px] overflow-y-auto">
                      {loadingBareAct ? (
                        <div className="flex items-center justify-center gap-2 text-slate-400 py-8">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Loading bare act text...
                        </div>
                      ) : (
                        <div className="text-slate-300 text-sm">
                          <MarkdownContent content={bareActContent || 'Loading...'} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Keyword Search Results */}
            {keywordResults.length > 0 && !selectedMapping && (
              <motion.div key="keyword-results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white">{keywordResults.length} Result{keywordResults.length !== 1 ? 's' : ''} Found</h2>
                  <span className="text-sm text-slate-400">Click a row to view mapping details</span>
                </div>

                <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="results-table">
                      <thead>
                        <tr className="bg-slate-900/50 border-b border-slate-700">
                          <th className="text-left px-6 py-4 text-cyan-400 font-semibold text-sm">Section (ITA 1961)</th>
                          <th className="text-left px-6 py-4 text-slate-400 font-semibold text-sm">Title / Description</th>
                          <th className="text-left px-6 py-4 text-amber-400 font-semibold text-sm">Section (ITA 2025)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keywordResults.slice(0, 100).map((section, index) => (
                          <motion.tr
                            key={`${section.section1961}-${index}`}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.02 }}
                            className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors cursor-pointer"
                            onClick={() => handleSelectFromKeywordResults(section)}
                          >
                            <td className="px-6 py-4"><span className="text-cyan-300 font-medium text-sm">{section.section1961}</span></td>
                            <td className="px-6 py-4"><span className="text-slate-300 text-sm">{section.title1961}</span></td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <span className="text-amber-300 font-medium text-sm">{section.section2025}</span>
                                {(section.isNew || section.section1961.toUpperCase() === 'NEW') && (
                                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-semibold rounded-full border border-green-500/30">NEW</span>
                                )}
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Empty State */}
            {!selectedMapping && keywordResults.length === 0 && !activeSearch && (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/20 to-amber-500/20 flex items-center justify-center mx-auto mb-4">
                  <Scale className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Start Your Search</h3>
                <p className="text-slate-400 max-w-md mx-auto">Use any of the three options above to find Section mappings between ITA 1961 and ITA 2025.</p>
              </motion.div>
            )}

            {/* No Results */}
            {activeSearch === 'keyword' && keywordResults.length === 0 && searchKeyword && (
              <motion.div key="no-results" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-slate-500" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">No Results Found</h3>
                <p className="text-slate-400">Try different keywords like "salary", "deduction", "house property", etc.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Footer */}
      <section className="py-8 px-4 border-t border-slate-800">
        <div className="w-[95%] max-w-4xl mx-auto text-center">
          <p className="text-sm text-slate-500">
            Data sourced from official Income Tax Bill Navigator. Section mappings are for reference purposes only.
            <br />
            For authoritative information, refer to the official{' '}
            <a href="https://incometaxindia.gov.in" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">
              Income Tax Department website
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SectionMapperPage;
